import axios from "axios";
import { resolveTrainDagPath } from "../../utils/modelPaths";
import React, { useEffect, useState, useRef, useMemo } from "react";
import NavigationBar from "../../components/NavBar/NavBar";
import DatasetWorkspaceShell from "../../components/Dataset/DatasetWorkspaceShell";
import "../../styles/Dataset.css";
import "../../styles/DatasetPage.css";
import "../../styles/org-buttons.css";
import "./NefPage.css";
import { Modal, Tabs, Tab, Form } from "react-bootstrap";
import Cookies from "js-cookie";
import jwtDecode from "jwt-decode";
import { buttonText } from "../../utils/buttonText";
import { notificationText } from "../../utils/notificationText";
import { pageText } from "../../utils/pageText";

function getModelStatusBadgeClass(status) {
    const normalized = String(status || "pending").toLowerCase();
    return normalized === "succeeded" ? "organizer" : "regular";
}

const isDino = model => model?.model_type === 'dino_classification';
const isFinished = status => ['succeeded', 'failed', 'error', 'terminated', 'skipped'].includes(String(status).toLowerCase());

function NefPage() {
    const userToken = Cookies.get("user_token");
    const userData = userToken ? jwtDecode(userToken) : null;
    const organization = userData ? userData.organization : null;
    const role = userData ? userData.role : null;  // 添加 role
    const username = userData ? (userData.username || userData.email) : null;

    const [allTrainedModels, setAllTrainedModels] = useState([]);
    const [showNefModal, setShowNefModal] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [nefFormData, setNefFormData] = useState({});
    const [defaultNefArgs, setDefaultNefArgs] = useState({});

    // 分组管理状态
    const [checkedModels, setCheckedModels] = useState([]);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [groupName, setGroupName] = useState("");
    const [showIpevaluatorModal, setShowIpevaluatorModal] = useState(false);
    const [ipevaluatorContent, setIpevaluatorContent] = useState("");
    const [ipevaluatorError, setIpevaluatorError] = useState("");
    const [ipevaluatorLoading, setIpevaluatorLoading] = useState(false);
    const [showIpEvalModal, setShowIpEvalModal] = useState(false);
    const [ipEvalFormData, setIpEvalFormData] = useState({});
    const [defaultIpEvalArgs, setDefaultIpEvalArgs] = useState({});
    const [ipEvalSelectedModel, setIpEvalSelectedModel] = useState(null);

    const loadTrainedModelsFromServer = async () => {
        try {
            // Admin 不传 organization 参数，后端会返回所有模型
            // 非 admin 传 organization，后端会只返回该组织的模型
            const params = {
                requesterToken: userToken
            };

            // 非 admin 用户才需要指定 organization
            if (role !== 'admin') {
                params.organization = organization || null;
            }

            const response = await axios.get("/kneron/trained-models", { params });
            setAllTrainedModels(response.data || []);
        } catch (error) {
            console.error("Error loading trained models from server:", error);
            const errorMsg = error.response?.data?.error || "Failed to load model cards from server.";
            window.alert(errorMsg);
            setAllTrainedModels([]);
        }
    };

    const saveTrainedModelsToServer = async (models) => {
        try {
            // 修改为新的请求格式：包含 requesterToken 和 models
            await axios.post("/kneron/trained-models", {
                requesterToken: userToken,
                models: models
            });
        } catch (error) {
            console.error("Error saving trained models to server:", error);
            window.alert("Failed to save model cards to server.");
        }
    };

    useEffect(() => {
        loadTrainedModelsFromServer();
    }, []);

    // 过滤模型：admin 显示所有模型（排除 Evaluation），非 admin 只显示自己组织的模型
    const allModels = allTrainedModels.filter(m => {
        // 排除 Evaluation 类型
        if (m.model_type === 'Evaluation') {
            return false;
        }

        // Admin 可以看到所有模型
        if (role === 'admin') {
            return true;
        }

        // 非 admin 只看自己组织的模型
        const orgMatch = organization ? (m.organization === organization) : !m.organization;
        return orgMatch;
    });

    const fetchUpdatedStatus = async () => {
        try {
            const currentModels = trainedModelsRef.current;

            const updatedModels = await Promise.all(
                currentModels.map(async (model) => {
                    let next = model;
                    if (isDino(model)) {
                        try {
                            const response = await axios.get(`/classification/runs/${model.run_id}`, {
                                params: { requesterToken: userToken }
                            });
                            const { metrics, ...run } = response.data;
                            return { ...model, ...run };
                        } catch (error) {
                            console.error(`DINO status check failed for ${model.run_id}:`, error);
                            return model;
                        }
                    }
                    if (model.nef_run_id && model.nef_status !== 'Succeeded' && model.nef_status !== 'Failed') {
                        try {
                            const response = await axios.get(`/kneron/kubeflow/status?run_id=${model.nef_run_id}`);
                            next = { ...next, nef_status: response.data.status };
                        } catch (error) {
                            console.error(`NEF status check failed for ${model.nef_run_id}:`, error);
                            next = { ...next, nef_status: 'Failed' };
                        }
                    }
                    if (model.ip_eval_run_id && model.ip_eval_status !== 'Succeeded' && model.ip_eval_status !== 'Failed') {
                        try {
                            const response = await axios.get(`/kneron/kubeflow/status?run_id=${model.ip_eval_run_id}`);
                            next = { ...next, ip_eval_status: response.data.status };
                        } catch (error) {
                            console.error(`IP eval status check failed for ${model.ip_eval_run_id}:`, error);
                            next = { ...next, ip_eval_status: 'Failed' };
                        }
                    }
                    return next;
                })
            );

            if (JSON.stringify(updatedModels) !== JSON.stringify(currentModels)) {
                setAllTrainedModels(updatedModels);
                await saveTrainedModelsToServer(updatedModels);
            }
        } catch (error) {
            console.error("Global NEF status update error:", error);
        }
    };

    useEffect(() => {
        const hasPending = allModels.some(model =>
            (isDino(model) && !isFinished(model.status)) ||
            (model.nef_run_id && !isFinished(model.nef_status))
            || (model.ip_eval_run_id && !isFinished(model.ip_eval_status))
        );
        if (hasPending) {
            const interval = setInterval(fetchUpdatedStatus, 10000);
            return () => clearInterval(interval);
        }
    }, [allModels]);

    const trainedModelsRef = useRef(allTrainedModels);
    useEffect(() => {
        trainedModelsRef.current = allTrainedModels;
    }, [allTrainedModels]);

    const handleGenerateNef = async (model) => {
        if (model.status !== "Succeeded") return;
        try {
            const response = await axios.get(`/kneron/supported_models/nef`);
            const foundNefModel = response.data;
            const defaults = isDino(model)
                ? {platform_name: model.platform_name || '720'}
                : foundNefModel.default_args || {};

            setSelectedModel(model);
            setDefaultNefArgs(defaults);
            setNefFormData({
                ...defaults,
                model_type: 'nef',
                // Bake source type now; selectedModel.model_type must not be lost.
                source_model_type: model.model_type,
            });

            if (defaults.platform_name) {
                const updatedModel = {
                    ...model,
                    platform_name: defaults.platform_name
                };
                setSelectedModel(updatedModel);
            }

            setShowNefModal(true);
        } catch (error) {
            console.error("Error fetching NEF model info:", error);
            window.alert("Failed to load NEF model information from backend");
        }
    };

    const handleInputChange = (key, value) => {
        setNefFormData(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleOpenIpEval = async (model) => {
        if (model.status !== "Succeeded") return;
        try {
            const response = await axios.get(`/kneron/supported_models/ip_eval`);
            const found = response.data;
            setIpEvalSelectedModel(model);
            setDefaultIpEvalArgs(found.default_args || {});
            setIpEvalFormData({
                ...found.default_args,
                platform_name:
                    model.platform_name
                    || model.chip_ver
                    || found.default_args?.platform_name
                    || "720",
                model_type: "ip_eval",
            });
            setShowIpEvalModal(true);
        } catch (error) {
            console.error("Error fetching IP eval model info:", error);
            window.alert("Failed to load IP Evaluation defaults from backend");
        }
    };

    const closeIpEvalModal = () => {
        setShowIpEvalModal(false);
        setIpEvalSelectedModel(null);
        setIpEvalFormData({});
    };

    const handleStartIpEval = async () => {
        if (!ipEvalSelectedModel) return;
        if (!userToken) {
            window.alert("Not logged in or token missing; please sign in again.");
            return;
        }
        try {
            const dag_path = resolveTrainDagPath(ipEvalSelectedModel);
            const params = {
                ...ipEvalSelectedModel,
                ...ipEvalFormData,
                dag_path,
                model_type: "ip_eval",
                requesterToken: userToken,
            };
            if (role !== "admin") {
                params.organization = organization;
            }
            const response = await axios.post(isDino(ipEvalSelectedModel)
                ? `/classification/runs/${ipEvalSelectedModel.run_id}/ip-evaluation`
                : "/kneron/train/start", params);
            const { run_id } = response.data;
            window.alert("IP Evaluation task has been submitted!");
            setShowIpEvalModal(false);
            const updatedModels = allTrainedModels.map((m) =>
                m.run_id === ipEvalSelectedModel.run_id
                    ? isDino(m) ? { ...m, ...response.data } : {
                        ...m,
                        ip_eval_run_id: run_id,
                        ip_eval_status: "pending",
                        platform_name: ipEvalFormData.platform_name || m.platform_name,
                        chip_ver: ipEvalFormData.platform_name || m.chip_ver,
                    }
                    : m
            );
            setAllTrainedModels(updatedModels);
            await saveTrainedModelsToServer(updatedModels);
            setIpEvalSelectedModel(null);
            setIpEvalFormData({});
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.response?.data?.description || error.response?.data || "An error occurred";
            window.alert("IP Evaluation failed: " + errorMsg);
        }
    };

    const handleStartNefGeneration = async () => {
        if (!selectedModel) return;
        if (!userToken) {
            window.alert("Not logged in or token missing; please sign in again.");
            return;
        }

        try {
            const params = {
                ...selectedModel,
                ...nefFormData,
                source_model_type:
                    selectedModel.model_type
                    || nefFormData.source_model_type
                    || "",
                dag_path: selectedModel.dag_path,
                requesterToken: userToken,
            };

            // 非 admin 用户才需要指定 organization
            if (role !== 'admin') {
                params.organization = organization;
            }

            const response = await axios.post(isDino(selectedModel)
                ? `/classification/runs/${selectedModel.run_id}/nef`
                : '/kneron/train/start', params);
            const { run_id } = response.data;

            window.alert("NEF generation task has been submitted!");
            setShowNefModal(false);

            const updatedModels = allTrainedModels.map(m =>
                m.run_id === selectedModel.run_id
                    ? isDino(m) ? { ...m, ...response.data }
                        : { ...m, nef_run_id: run_id, nef_status: 'pending', platform_name: nefFormData.platform_name}
                    : m
            );

            setAllTrainedModels(updatedModels);
            await saveTrainedModelsToServer(updatedModels);

            setSelectedModel(null);
            setNefFormData({});
        } catch (error) {
            let errorMsg = error.response?.data?.error || error.response?.data?.description || error.response?.data || "An error occurred";
            window.alert("NEF generation failed: " + errorMsg);
        }
    };

    const handleDownloadNef = async (model) => {
        try {
            const platform = isDino(model) ? model.nef_platform : model.platform_name;
            const response = await axios.post(isDino(model)
                ? `/classification/runs/${model.run_id}/nef/artifacts` : '/kneron/downloadNef', {
                ...(isDino(model) ? {requesterToken: userToken} : {}),
                platform_name: platform,
                dag_path: model.dag_path
            }, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `nef_${model.model_name}_${platform}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            window.alert("NEF file download succeeded!");
        } catch (error) {
            console.error("Failed to download NEF:", error);
            let errorMsg = "Download failed";

            if (error.response?.status === 404) {
                try {
                    const errorData = JSON.parse(error.response.data);
                    errorMsg = `File not found: ${errorData.file_path}`;
                } catch (e) {
                    errorMsg = "NEF file not found";
                }
            } else if (error.response?.data) {
                errorMsg = error.response.data;
            }

            window.alert(errorMsg);
        }
    };



    const handleShowIpevaluator = async (trainedModel) => {
        const dag_path = resolveTrainDagPath(trainedModel);
        setIpevaluatorLoading(true);
        setIpevaluatorContent("");
        setIpevaluatorError("");
        setShowIpevaluatorModal(true);
        try {
            const res = await axios.post(isDino(trainedModel)
                ? `/classification/runs/${trainedModel.run_id}/ip-evaluator` : '/kneron/eval/ipevaluator', {
                ...(isDino(trainedModel) ? {requesterToken: userToken, platform_name: trainedModel.ip_eval_platform} : {}),
                dag_path,
                Model_ID: trainedModel.Model_ID,
                createdDate: trainedModel.createdDate,
                model_name: trainedModel.model_name,
            });
            setIpevaluatorContent(res.data?.content || "");
        } catch (e) {
            const msg = e.response?.data?.error || e.response?.data || "Failed to read Ipevaluator.txt";
            setIpevaluatorError(typeof msg === "string" ? msg : JSON.stringify(msg));
        }
        setIpevaluatorLoading(false);
    };

    const handleDeleteModel = async (modelToDelete) => {
        if (!modelToDelete) return;

        const confirmDelete = window.confirm(`Are you sure you want to delete the model "${modelToDelete.model_name || modelToDelete.solution_name}"?`);

        if (confirmDelete) {
            try {
                const newModelsList = allTrainedModels.filter(m => m.run_id !== modelToDelete.run_id);
                setAllTrainedModels(newModelsList);
                await saveTrainedModelsToServer(newModelsList);

                // 从选中列表中移除
                setCheckedModels(prev => prev.filter(id => id !== modelToDelete.run_id));

                window.alert("Model deleted successfully!");
            } catch (error) {
                console.error("Error deleting model:", error);
                const errorMsg = error.response?.data?.error || error.message || "Delete failed";
                window.alert(`Delete failed: ${errorMsg}`);
            }
        }
    };

    const closeNefModal = () => {
        setShowNefModal(false);
        setSelectedModel(null);
        setNefFormData({});
    };

    // 分组相关函数
    const handleGroupModels = () => {
        if (!checkedModels || checkedModels.length === 0) {
            window.alert("Please select models.");
            return;
        }
        setShowGroupModal(true);
    };

    const moveToGroup = async () => {
        if (!groupName) {
            window.alert("Please input a group name.");
            return;
        }
        try {
            const response = await axios.post('/kneron/nef-models/group', {
                model_ids: checkedModels,
                group_name: groupName
            });
            window.alert(`Models moved to group "${groupName}" successfully!`);
            setShowGroupModal(false);
            setGroupName("");
            setCheckedModels([]);
            await loadTrainedModelsFromServer();
        } catch (error) {
            const errorMsg = error.response?.data?.error || "Failed to move models to group";
            window.alert(errorMsg);
        }
    };

    const removeFromGroup = async () => {
        if (!checkedModels || checkedModels.length === 0) {
            window.alert("Please select models.");
            return;
        }
        try {
            const response = await axios.delete('/kneron/nef-models/group', {
                data: {
                    model_ids: checkedModels
                }
            });
            window.alert("Models removed from group successfully!");
            setShowGroupModal(false);
            setCheckedModels([]);
            await loadTrainedModelsFromServer();
        } catch (error) {
            const errorMsg = error.response?.data?.error || "Failed to remove models from group";
            window.alert(errorMsg);
        }
    };

    const handleDeleteSelected = async () => {
        if (!checkedModels || checkedModels.length === 0) {
            window.alert("Please select models.");
            return;
        }

        const confirmDelete = window.confirm(`Are you sure you want to delete ${checkedModels.length} selected model(s)?`);
        if (!confirmDelete) return;

        try {
            const modelsToDelete = allTrainedModels.filter(m => checkedModels.includes(m.run_id));

            const response = await axios.delete('/kneron/nef-models/selected', {
                data: {
                    models: modelsToDelete
                }
            });

            window.alert(`Successfully deleted ${response.data.deleted_count} model(s)!`);
            setCheckedModels([]);
            await loadTrainedModelsFromServer();
        } catch (error) {
            const errorMsg = error.response?.data?.error || "Failed to delete models";
            window.alert(errorMsg);
        }
    };

    const handleCheckModel = (runId) => {
        setCheckedModels(prev => {
            if (prev.includes(runId)) {
                return prev.filter(id => id !== runId);
            } else {
                return [...prev, runId];
            }
        });
    };

    // 组织模型数据
    const groupedModels = useMemo(() => {
        const groups = {};
        const ungrouped = [];

        allModels.forEach(model => {
            if (model.group) {
                if (!groups[model.group]) {
                    groups[model.group] = [];
                }
                groups[model.group].push(model);
            } else {
                ungrouped.push(model);
            }
        });

        return { groups, ungrouped };
    }, [allModels]);

    const nefStats = useMemo(() => ({
        total: allModels.length,
        ready: allModels.filter(m => m.status === "Succeeded").length,
        generating: allModels.filter(m => m.nef_run_id && !["Succeeded", "Failed"].includes(m.nef_status)).length,
        completed: allModels.filter(m => m.nef_status === "Succeeded").length,
    }), [allModels]);

    // 模型卡片组件
    const ModelCard = ({ trainedModel, index }) => {
        const isChecked = checkedModels.includes(trainedModel.run_id);
        const hardwareBusy = isDino(trainedModel) && (
            (trainedModel.ip_eval_run_id && !isFinished(trainedModel.ip_eval_status))
            || (trainedModel.nef_run_id && !isFinished(trainedModel.nef_status)));
        const canGenerateNef = trainedModel.status === "Succeeded"
            && (!isDino(trainedModel) || !!trainedModel.hardware_version);
        const canReadIpReport = isDino(trainedModel)
            ? !!trainedModel.ip_eval_run_id && trainedModel.hardware_version && isFinished(trainedModel.ip_eval_status)
            : trainedModel.ip_eval_status === 'Succeeded';

        return (
            <div key={index} className="model-card">
                {/* 复选框 */}
                <Form.Check
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleCheckModel(trainedModel.run_id)}
                    style={{
                        position: 'absolute',
                        top: '12px',
                        left: '12px',
                        zIndex: 10
                    }}
                />

                {/* 删除按钮 */}
                <button
                    className="close-btn"
                    onClick={() => handleDeleteModel(trainedModel)}
                >
                    ×
                </button>

                <div className="model-card-header">
                    <div>
                        <span className="model-card-kicker">{trainedModel.model_type || pageText("Model")}</span>
                        <h3 title={trainedModel.model_name || trainedModel.solution_name}>
                            {trainedModel.model_name || trainedModel.solution_name}
                        </h3>
                    </div>
                    <span className={`org-role-badge ${getModelStatusBadgeClass(trainedModel.nef_run_id ? trainedModel.nef_status : trainedModel.status)}`}>
                        {pageText(trainedModel.nef_run_id ? (trainedModel.nef_status || "pending") : trainedModel.status)}
                    </span>
                </div>

                <div className="model-meta-grid">
                    {trainedModel.Model_ID && <div><span>{pageText("Model ID")}</span><strong>{trainedModel.Model_ID}</strong></div>}
                    {trainedModel.cls_mapping && <div><span>{pageText("Mapping")}</span><strong>{trainedModel.cls_mapping}</strong></div>}
                    {trainedModel.platform_name && <div><span>{pageText("Platform")}</span><strong>{trainedModel.platform_name}</strong></div>}
                    {trainedModel.group && <div><span>{pageText("Group")}</span><strong>{trainedModel.group}</strong></div>}
                </div>
                <div className="model-card-footer">
                    {canGenerateNef && (
                        <button
                            type="button"
                            className="org-btn org-btn-secondary"
                            onClick={() => handleOpenIpEval(trainedModel)}
                            disabled={isDino(trainedModel) ? hardwareBusy : (
                                !!trainedModel.ip_eval_run_id
                                && trainedModel.ip_eval_status !== "Succeeded"
                                && trainedModel.ip_eval_status !== "Failed")
                            }
                        >
                            {buttonText(trainedModel.ip_eval_run_id
                                && !isFinished(trainedModel.ip_eval_status)
                                ? "IP Evaluating..."
                                : "IP Evaluation")}
                        </button>
                    )}
                    <button
                        type="button"
                        className="org-btn org-btn-primary"
                        onClick={() => handleGenerateNef(trainedModel)}
                        disabled={!canGenerateNef || hardwareBusy}
                    >
                        {buttonText("Generate NEF")}
                    </button>
                    {(canReadIpReport || trainedModel.nef_status === "Succeeded") && (
                        <div className="model-card-link-row model-card-corner-links">
                            <div className="model-card-corner-left">
                                {canReadIpReport && (
                                    <button
                                        type="button"
                                        className="model-card-text-link"
                                        onClick={() => handleShowIpevaluator(trainedModel)}
                                    >
                                        {buttonText("View IP Evaluator")}
                                    </button>
                                )}
                            </div>
                            <div className="model-card-corner-right">
                                {trainedModel.nef_status === "Succeeded" && (
                                    <button
                                        type="button"
                                        className="model-card-text-link"
                                        onClick={() => handleDownloadNef(trainedModel)}
                                    >
                                        {buttonText("Download NEF")}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // 分组模态框
    const GroupModal = (
        <Modal show={showGroupModal} onHide={() => setShowGroupModal(false)}>
            <Modal.Header closeButton closeLabel={buttonText("Close")}>
                <Modal.Title>Group Management</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Selected {checkedModels.length} model(s)</p>
                <Form.Group>
                    <Form.Label>Group Name:</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter group name"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer className="org-modal-footer">
                <button type="button" className="org-btn org-btn-secondary" onClick={() => setShowGroupModal(false)}>
                    {buttonText("Cancel")}
                </button>
                <button type="button" className="org-btn org-btn-danger" onClick={removeFromGroup}>
                    {buttonText("Remove from Group")}
                </button>
                <button type="button" className="org-btn org-btn-primary" onClick={moveToGroup}>
                    {buttonText("Move to Group")}
                </button>
            </Modal.Footer>
        </Modal>
    );

    return (
        <div>
            <NavigationBar />
            <DatasetWorkspaceShell mainClassName="nef-page" showGroups={false}>
            <section className="model-page-header">
                <div>
                    <span className="model-page-eyebrow">{pageText("Deployment Artifact")}</span>
                    <h1>{pageText("Generate NEF")}</h1>
                    <p>{pageText("Convert succeeded training outputs into platform-ready NEF packages.")}</p>
                </div>
                <div className="model-page-stats">
                    <div><span>{pageText("Total")}</span><strong>{nefStats.total}</strong></div>
                    <div><span>{pageText("Ready")}</span><strong>{nefStats.ready}</strong></div>
                    <div><span>{pageText("Generating")}</span><strong>{nefStats.generating}</strong></div>
                    <div><span>{pageText("Completed")}</span><strong>{nefStats.completed}</strong></div>
                </div>
            </section>

            <div className="model-workspace-toolbar">
                <div className="model-toolbar-actions">
                <button
                    type="button"
                    className="org-btn org-btn-secondary"
                    onClick={handleGroupModels}
                    disabled={checkedModels.length === 0}
                >
                    {buttonText("Group")}
                </button>
                <button
                    type="button"
                    className="org-btn org-btn-danger"
                    onClick={handleDeleteSelected}
                    disabled={checkedModels.length === 0}
                >
                    {buttonText("Delete Selected")}
                </button>
                </div>
            </div>

            <div className="model-tabs-panel">
                <Tabs defaultActiveKey="all" className="mb-3">
                    <Tab eventKey="all" title={buttonText("All Models")}>
                        <div className="model-cards-container">
                            {allModels.length === 0 ? (
                                <div className="model-empty-state">{pageText("No eligible models yet. Complete training before generating NEF.")}</div>
                            ) : allModels.map((trainedModel, index) => (
                                <ModelCard key={trainedModel.run_id || index} trainedModel={trainedModel} index={index} />
                            ))}
                        </div>
                    </Tab>

                    {Object.keys(groupedModels.groups).map(groupKey => (
                        <Tab
                            key={groupKey}
                            eventKey={groupKey}
                            title={
                                <span>
                                    {groupKey}
                                    <button
                                        className="btn-close model-tab-close"
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            const confirmDelete = window.confirm(`Delete group "${groupKey}"? Models will be ungrouped.`);
                                            if (confirmDelete) {
                                                try {
                                                    const groupModelIds = groupedModels.groups[groupKey].map(m => m.run_id);
                                                    await axios.delete('/kneron/nef-models/group', {
                                                        data: { model_ids: groupModelIds }
                                                    });
                                                    await loadTrainedModelsFromServer();
                                                    window.alert(`Group "${groupKey}" deleted successfully!`);
                                                } catch (error) {
                                                    window.alert("Failed to delete group");
                                                }
                                            }
                                        }}
                                    />
                                </span>
                            }
                        >
                            <div className="model-cards-container">
                                {groupedModels.groups[groupKey].map((trainedModel, index) => (
                                    <ModelCard key={trainedModel.run_id || index} trainedModel={trainedModel} index={index} />
                                ))}
                            </div>
                        </Tab>
                    ))}
                </Tabs>
            </div>

            {/* 模态框 */}
            {GroupModal}

            <Modal show={showNefModal} onHide={closeNefModal} className="nef-generate-modal" centered>
                <Modal.Header closeButton closeLabel={buttonText("Close")}>
                    <Modal.Title>
                        Generate NEF for: {selectedModel?.model_name || selectedModel?.solution_name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="nef-generate-form">
                        {Object.entries(defaultNefArgs).map(([key]) => (
                            <div key={key} className="nef-generate-field">
                                <label htmlFor={`nef-${key}`}>{key}</label>
                                <input
                                    id={`nef-${key}`}
                                    type="text"
                                    inputMode="numeric"
                                    value={nefFormData[key] || ""}
                                    onChange={(e) => handleInputChange(key, e.target.value)}
                                    placeholder={`Enter ${key}`}
                                />
                            </div>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer className="org-modal-footer">
                    <button
                        type="button"
                        className="org-btn org-btn-secondary"
                        onClick={closeNefModal}
                    >
                        {buttonText("Cancel")}
                    </button>
                    <button
                        type="button"
                        className="org-btn org-btn-primary"
                        onClick={handleStartNefGeneration}
                    >
                        {buttonText("Start")}
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={showIpEvalModal} onHide={closeIpEvalModal} className="nef-generate-modal" centered>
                <Modal.Header closeButton closeLabel={buttonText("Close")}>
                    <Modal.Title>
                        IP Evaluation for: {ipEvalSelectedModel?.model_name || ipEvalSelectedModel?.solution_name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="nef-generate-form">
                        {Object.entries(defaultIpEvalArgs).map(([key]) => (
                            <div key={key} className="nef-generate-field">
                                <label htmlFor={`ip-eval-${key}`}>{key}</label>
                                <input
                                    id={`ip-eval-${key}`}
                                    type="text"
                                    inputMode="numeric"
                                    value={ipEvalFormData[key] || ""}
                                    onChange={(e) => setIpEvalFormData(prev => ({
                                        ...prev,
                                        [key]: e.target.value,
                                    }))}
                                    placeholder={`Enter ${key}`}
                                />
                            </div>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer className="org-modal-footer">
                    <button
                        type="button"
                        className="org-btn org-btn-secondary"
                        onClick={closeIpEvalModal}
                    >
                        {buttonText("Cancel")}
                    </button>
                    <button
                        type="button"
                        className="org-btn org-btn-primary"
                        onClick={handleStartIpEval}
                    >
                        {buttonText("Start")}
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={showIpevaluatorModal} onHide={() => setShowIpevaluatorModal(false)} size="lg">
                <Modal.Header closeButton closeLabel={buttonText("Close")}>
                    <Modal.Title>IP Evaluator</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                    {ipevaluatorLoading ? (
                        <p>{pageText("Loading...")}</p>
                    ) : ipevaluatorError ? (
                        <p style={{ color: 'red' }}>
                            {notificationText(ipevaluatorError)}
                        </p>
                    ) : ipevaluatorContent ? (
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{ipevaluatorContent}</pre>
                    ) : (
                        <p>{notificationText("No Data")}</p>
                    )}
                </Modal.Body>
                <Modal.Footer className="org-modal-footer">
                    <button
                        type="button"
                        className="org-btn org-btn-secondary"
                        onClick={() => setShowIpevaluatorModal(false)}
                    >
                        {buttonText("Close")}
                    </button>
                </Modal.Footer>
            </Modal>
            </DatasetWorkspaceShell>
        </div>
    );
}

export default NefPage;
